import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import api, { errMsg } from "../api";
import { CheckSquare, Square, Plus, Trash2, Loader2, Calendar as CalIcon, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const PRIO_COLORS = {
    low: "bg-neutral-100 text-neutral-700",
    medium: "bg-blue-100 text-blue-800",
    high: "bg-red-100 text-red-800",
};

export default function TodosPage() {
    const { lang } = useAuth();
    const [todos, setTodos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newOpen, setNewOpen] = useState(false);
    const [showCompleted, setShowCompleted] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/todos");
            setTodos(data);
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Silent background refresh when AppLayout polls (every 5s)
    useEffect(() => {
        const h = () => load();
        window.addEventListener("mytacs:tick", h);
        return () => window.removeEventListener("mytacs:tick", h);
    }, [load]);

    const toggle = async (todo) => {
        try {
            await api.patch(`/todos/${todo.id}`, { completed: !todo.completed });
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/todos/${id}`);
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const open = todos.filter((t) => !t.completed);
    const completed = todos.filter((t) => t.completed);

    return (
        <div className="px-4 sm:px-8 py-8 max-w-4xl" data-testid="todos-page">
            <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1.5">
                        <CheckSquare className="w-3 h-3" /> {t(lang, "myTodos")}
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold mt-1">{t(lang, "myTodos")}</h1>
                    <p className="text-sm text-neutral-500 mt-1">{t(lang, "openTodos")}: {open.length} · {t(lang, "completedTodos")}: {completed.length}</p>
                </div>
                <Button onClick={() => setNewOpen(true)} data-testid="new-todo-btn" className="bg-neutral-900 hover:bg-neutral-800">
                    <Plus className="w-4 h-4 mr-1.5" /> {t(lang, "newTodo")}
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                </div>
            ) : todos.length === 0 ? (
                <div className="border border-dashed border-neutral-300 rounded-lg py-16 text-center" data-testid="empty-todos">
                    <CheckSquare className="w-12 h-12 mx-auto text-neutral-300 mb-2" />
                    <p className="text-neutral-500">{t(lang, "noTodos")}</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {open.map((todo) => (
                        <TodoItem key={todo.id} todo={todo} onToggle={toggle} onDelete={remove} lang={lang} />
                    ))}
                    {completed.length > 0 && (
                        <div className="pt-4">
                            <button
                                onClick={() => setShowCompleted(!showCompleted)}
                                data-testid="toggle-completed-btn"
                                className="text-xs uppercase font-semibold tracking-wider text-neutral-500 hover:text-neutral-900 flex items-center gap-1"
                            >
                                {showCompleted ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                {t(lang, "completedTodos")} ({completed.length})
                            </button>
                            {showCompleted && (
                                <div className="mt-2 space-y-2 opacity-70">
                                    {completed.map((todo) => (
                                        <TodoItem key={todo.id} todo={todo} onToggle={toggle} onDelete={remove} lang={lang} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <NewTodoDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={load} lang={lang} />
        </div>
    );
}

function TodoItem({ todo, onToggle, onDelete, lang }) {
    const overdue = todo.deadline && !todo.completed && new Date(todo.deadline) < new Date();
    return (
        <div
            data-testid={`todo-${todo.id}`}
            className={`group flex items-start gap-3 p-3 bg-white border rounded-lg hover:shadow-sm transition ${overdue ? "border-red-200 bg-red-50/30" : "border-neutral-200"}`}
        >
            <button
                onClick={() => onToggle(todo)}
                data-testid={`todo-toggle-${todo.id}`}
                className="mt-0.5 shrink-0"
            >
                {todo.completed ? (
                    <CheckSquare className="w-5 h-5 text-emerald-600" />
                ) : (
                    <Square className="w-5 h-5 text-neutral-400 hover:text-neutral-700" />
                )}
            </button>
            <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${todo.completed ? "line-through text-neutral-400" : "text-neutral-900"}`}>
                    {todo.title}
                </p>
                {todo.description && (
                    <p className={`text-xs mt-0.5 ${todo.completed ? "text-neutral-400" : "text-neutral-600"}`}>{todo.description}</p>
                )}
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${PRIO_COLORS[todo.priority]}`}>
                        {t(lang, todo.priority)}
                    </span>
                    {todo.deadline && (
                        <span className={`text-[11px] flex items-center gap-1 ${overdue ? "text-red-700 font-semibold" : "text-neutral-500"}`}>
                            <CalIcon className="w-3 h-3" />
                            {new Date(todo.deadline).toLocaleDateString(LOCALES[lang])}
                        </span>
                    )}
                </div>
            </div>
            <button
                onClick={() => onDelete(todo.id)}
                data-testid={`todo-delete-${todo.id}`}
                className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-600 p-1 shrink-0 transition"
                title={t(lang, "delete")}
            >
                <Trash2 className="w-4 h-4" />
            </button>
        </div>
    );
}

function NewTodoDialog({ open, onClose, onCreated, lang }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [priority, setPriority] = useState("medium");
    const [deadline, setDeadline] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!title.trim()) return;
        setBusy(true);
        try {
            await api.post("/todos", {
                title,
                description,
                priority,
                deadline: deadline ? new Date(deadline).toISOString() : null,
            });
            toast.success(t(lang, "newTodo"));
            setTitle(""); setDescription(""); setPriority("medium"); setDeadline("");
            onCreated();
            onClose();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="new-todo-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display">{t(lang, "newTodo")}</DialogTitle>
                    <DialogDescription className="text-xs text-neutral-500">{t(lang, "myTodos")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "title")}</Label>
                        <Input
                            data-testid="todo-title-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="mt-1.5"
                            autoFocus
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "description")}</Label>
                        <Textarea
                            data-testid="todo-description-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            className="mt-1.5"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "priority")}</Label>
                            <Select value={priority} onValueChange={setPriority}>
                                <SelectTrigger className="mt-1.5" data-testid="todo-priority-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">{t(lang, "low")}</SelectItem>
                                    <SelectItem value="medium">{t(lang, "medium")}</SelectItem>
                                    <SelectItem value="high">{t(lang, "high")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "deadline")}</Label>
                            <Input
                                type="date"
                                data-testid="todo-deadline-input"
                                value={deadline}
                                onChange={(e) => setDeadline(e.target.value)}
                                className="mt-1.5"
                            />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="todo-cancel-btn">{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy || !title.trim()} data-testid="todo-create-btn" className="bg-neutral-900 hover:bg-neutral-800">
                        {t(lang, "create")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
